/**
 * OpenAI Model Pricing Configuration
 * Prices are in USD per 1 million tokens
 * Last updated: July 2026
 *
 * Source: https://developers.openai.com/api/docs/pricing
 *
 * Mirror of back-end/shared/shared/model_pricing.py, which is what the API
 * costs LLM_REQUESTS rows with. Keep the two in step: this table only prices
 * rows the API returned without a cost, so a divergence shows up as the same
 * request costing two different amounts in two places.
 */

export interface ModelPricing {
  inputPricePerMillion: number;
  outputPricePerMillion: number;
  displayName: string;
}

/**
 * Pricing table for OpenAI models
 * Keys should match model names as stored in the database
 */
export const MODEL_PRICING: Record<string, ModelPricing> = {
  // GPT-5.6 family (released Jul 2026). Bare "gpt-5.6" is an alias OpenAI
  // routes to Sol, so it is priced as Sol.
  "gpt-5.6": {
    inputPricePerMillion: 5.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-5.6"
  },
  "gpt-5.6-sol": {
    inputPricePerMillion: 5.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-5.6 Sol"
  },
  "gpt-5.6-terra": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 15.00,
    displayName: "GPT-5.6 Terra"
  },
  "gpt-5.6-luna": {
    inputPricePerMillion: 1.00,
    outputPricePerMillion: 6.00,
    displayName: "GPT-5.6 Luna"
  },
  "gpt-5.6-chat-latest": {
    inputPricePerMillion: 5.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-5.6 Chat"
  },

  // GPT-5.5 family
  "gpt-5.5": {
    inputPricePerMillion: 5.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-5.5"
  },
  "gpt-5.5-chat-latest": {
    inputPricePerMillion: 5.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-5.5 Chat"
  },
  "gpt-5.5-pro": {
    inputPricePerMillion: 30.00,
    outputPricePerMillion: 180.00,
    displayName: "GPT-5.5 Pro"
  },

  // GPT-5.4 family
  "gpt-5.4": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 15.00,
    displayName: "GPT-5.4"
  },
  "gpt-5.4-mini": {
    inputPricePerMillion: 0.75,
    outputPricePerMillion: 4.50,
    displayName: "GPT-5.4 Mini"
  },
  "gpt-5.4-nano": {
    inputPricePerMillion: 0.20,
    outputPricePerMillion: 1.25,
    displayName: "GPT-5.4 Nano"
  },
  "gpt-5.4-pro": {
    inputPricePerMillion: 30.00,
    outputPricePerMillion: 180.00,
    displayName: "GPT-5.4 Pro"
  },

  // GPT-5.2 family - MODEL_DEFAULT for core-agent and every MCP server
  "gpt-5.2": {
    inputPricePerMillion: 1.75,
    outputPricePerMillion: 14.00,
    displayName: "GPT-5.2"
  },
  "gpt-5.2-chat-latest": {
    inputPricePerMillion: 1.75,
    outputPricePerMillion: 14.00,
    displayName: "GPT-5.2 Chat"
  },
  "gpt-5.2-pro": {
    inputPricePerMillion: 21.00,
    outputPricePerMillion: 168.00,
    displayName: "GPT-5.2 Pro"
  },

  // GPT-4.1 family - the enrichment pipeline's CHAT_MODEL_NAME and the
  // AZURE_AI_FOUNDRY_DEPLOYMENT in every environment's helm values
  "gpt-4.1": {
    inputPricePerMillion: 2.00,
    outputPricePerMillion: 8.00,
    displayName: "GPT-4.1"
  },
  "gpt-4.1-mini": {
    inputPricePerMillion: 0.40,
    outputPricePerMillion: 1.60,
    displayName: "GPT-4.1 Mini"
  },
  "gpt-4.1-nano": {
    inputPricePerMillion: 0.10,
    outputPricePerMillion: 0.40,
    displayName: "GPT-4.1 Nano"
  },

  // GPT-5.1 models (released Nov 2025)
  // Same pricing as GPT-5: $1.25 input, $10 output per 1M tokens
  "gpt-5.1": {
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 10.00,
    displayName: "GPT-5.1"
  },
  "gpt-5.1-chat-latest": {
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 10.00,
    displayName: "GPT-5.1 Chat"
  },

  // GPT-5 models (released Aug 2025)
  "gpt-5": {
    inputPricePerMillion: 1.25,
    outputPricePerMillion: 10.00,
    displayName: "GPT-5"
  },
  "gpt-5-pro": {
    inputPricePerMillion: 15.00,
    outputPricePerMillion: 120.00,
    displayName: "GPT-5 Pro"
  },

  // GPT-5 mini models - faster/cheaper tier
  "gpt-5-mini": {
    inputPricePerMillion: 0.25,
    outputPricePerMillion: 2.00,
    displayName: "GPT-5 Mini"
  },

  // GPT-5 nano models - fastest/cheapest tier
  "gpt-5-nano": {
    inputPricePerMillion: 0.05,
    outputPricePerMillion: 0.40,
    displayName: "GPT-5 Nano"
  },

  // GPT-4o models (previous generation)
  "gpt-4o": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 10.00,
    displayName: "GPT-4o"
  },
  "gpt-4o-2024-11-20": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 10.00,
    displayName: "GPT-4o (Nov 2024)"
  },
  "gpt-4o-2024-08-06": {
    inputPricePerMillion: 2.50,
    outputPricePerMillion: 10.00,
    displayName: "GPT-4o (Aug 2024)"
  },

  // GPT-4o mini models
  "gpt-4o-mini": {
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.60,
    displayName: "GPT-4o Mini"
  },
  "gpt-4o-mini-2024-07-18": {
    inputPricePerMillion: 0.15,
    outputPricePerMillion: 0.60,
    displayName: "GPT-4o Mini (Jul 2024)"
  },

  // GPT-4 Turbo (legacy)
  "gpt-4-turbo": {
    inputPricePerMillion: 10.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-4 Turbo"
  },
  "gpt-4-turbo-2024-04-09": {
    inputPricePerMillion: 10.00,
    outputPricePerMillion: 30.00,
    displayName: "GPT-4 Turbo (Apr 2024)"
  },

  // GPT-4 (legacy)
  "gpt-4": {
    inputPricePerMillion: 30.00,
    outputPricePerMillion: 60.00,
    displayName: "GPT-4"
  },

  // GPT-3.5 Turbo (legacy)
  "gpt-3.5-turbo": {
    inputPricePerMillion: 0.50,
    outputPricePerMillion: 1.50,
    displayName: "GPT-3.5 Turbo"
  },
  "gpt-3.5-turbo-0125": {
    inputPricePerMillion: 0.50,
    outputPricePerMillion: 1.50,
    displayName: "GPT-3.5 Turbo (Jan 2025)"
  },
};

/**
 * Default pricing for unknown models. Tracks whatever the primary model is —
 * now gpt-5.2 (MODEL_DEFAULT everywhere), previously gpt-5.1. An unrecognised
 * model is most likely a newer one, so guessing at the current default's rate
 * beats guessing at a superseded one's.
 */
export const DEFAULT_PRICING: ModelPricing = {
  inputPricePerMillion: 1.75,
  outputPricePerMillion: 14.00,
  displayName: "Unknown Model"
};

/**
 * Get pricing for a model, falling back to default if not found
 */
export function getModelPricing(modelName: string): ModelPricing {
  // Try exact match first
  if (MODEL_PRICING[modelName]) {
    return MODEL_PRICING[modelName];
  }

  // Try case-insensitive match
  const lowerModel = modelName.toLowerCase();
  for (const [key, pricing] of Object.entries(MODEL_PRICING)) {
    if (key.toLowerCase() === lowerModel) {
      return pricing;
    }
  }

  // Try partial match, so a dated snapshot ("gpt-5.4-mini-2026-01") is priced
  // as its base model. Longest key wins, so "gpt-5" cannot claim a name that
  // "gpt-5.4-mini" also matches.
  //
  // Only the model-extends-key direction counts. Matching the other way round
  // let a bare family name inherit a longer key's price — "gpt-5.4" was costed
  // at "gpt-5.4-mini" rates, a third of the real flagship price — and every
  // such name is one this table should list outright anyway.
  let bestMatch: ModelPricing | null = null;
  let bestLen = 0;
  for (const [key, pricing] of Object.entries(MODEL_PRICING)) {
    const k = key.toLowerCase();
    if (lowerModel.startsWith(k) && k.length > bestLen) {
      bestMatch = pricing;
      bestLen = k.length;
    }
  }
  if (bestMatch) {
    return bestMatch;
  }

  return DEFAULT_PRICING;
}

/**
 * Calculate cost for a single LLM request
 * @param modelName - The model used
 * @param promptTokens - Number of input tokens
 * @param completionTokens - Number of output tokens
 * @returns Cost in USD
 */
export function calculateCost(
  modelName: string,
  promptTokens: number | null,
  completionTokens: number | null
): number | null {
  if (promptTokens === null && completionTokens === null) {
    return null;
  }

  const pricing = getModelPricing(modelName);
  const inputCost = (promptTokens || 0) * (pricing.inputPricePerMillion / 1_000_000);
  const outputCost = (completionTokens || 0) * (pricing.outputPricePerMillion / 1_000_000);

  return inputCost + outputCost;
}

/**
 * Calculate aggregated cost using default pricing (for rollups across potentially mixed models)
 * @deprecated Use getTotalCostFromApi() instead - the API now provides pre-calculated costs from the database
 * @param promptTokens - Total number of input tokens
 * @param completionTokens - Total number of output tokens
 * @returns Cost in USD or null if no tokens
 */
export function calculateAggregatedCost(
  promptTokens: number | null,
  completionTokens: number | null
): number | null {
  if (promptTokens === null && completionTokens === null) {
    return null;
  }

  const inputCost = (promptTokens || 0) * (DEFAULT_PRICING.inputPricePerMillion / 1_000_000);
  const outputCost = (completionTokens || 0) * (DEFAULT_PRICING.outputPricePerMillion / 1_000_000);

  return inputCost + outputCost;
}

/**
 * Get total cost from API-provided input and output costs
 * This uses the costs calculated at the time of the LLM request (stored in database)
 * which are more accurate than recalculating based on tokens alone.
 * @param inputCostUsd - Input cost in USD (from API)
 * @param outputCostUsd - Output cost in USD (from API)
 * @returns Total cost in USD or null if no costs available
 */
export function getTotalCostFromApi(
  inputCostUsd: number | null,
  outputCostUsd: number | null
): number | null {
  if (inputCostUsd === null && outputCostUsd === null) {
    return null;
  }

  return (inputCostUsd || 0) + (outputCostUsd || 0);
}

/**
 * Format cost for display
 * @param cost - Cost in USD
 * @returns Formatted cost string
 */
export function formatCost(cost: number | null): string {
  if (cost === null) return 'N/A';
  if (cost < 0.01) return `$${cost.toFixed(6)}`;
  if (cost < 1) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}
